import React from 'react';
import { Box, CircularProgress, Grid } from '@material-ui/core';
import { AccordionSummary } from '@material-ui/core';
import { injectIntl, IntlShape } from 'react-intl';
import { get } from 'lodash';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import StatusChip from 'components/StatusChip';
import {
  BOX_SIZE_BY_REQUEST_STATUS,
  StyledCaption,
  StyledShortBody,
} from 'components/PACRequests/styles';
import { StyledAccordionDetails } from 'containers/AmcDetailsPage/PacRequestsTab/MyRequests/styles';
import messages from 'components/PACRequests/messages';
import { translate } from 'helpers/intl';
import { REQUESTS_STATUSES } from 'constants/amcStatuses';
import { ShortBody } from 'components/Typography';

interface RequestDetailsType {
  intl: IntlShape;
  request: {
    date: string;
    Statut: string;
    Reference: string;
    IdCategorie: string;
    DateCreation: string;
    NomPrenomPAC: string;
    DateNaissance: string;
    DateNaissanceFormatted: string;
    Genre: string;
    MotifRejet: string;
    DateDemande: string;
    ReferenceDemande: string;
    LibelleMotifRejet: string;
  };
  isLoading: boolean;
  expanded: boolean | String;
  index: string;
}

const RequestDetails = ({
  request,
  expanded,
  index,
  isLoading,
  ...props
}: RequestDetailsType) => {
  const renderDetails = (item: any) => {
    switch (item.Statut) {
      case REQUESTS_STATUSES.CREATED:
        /* istanbul ignore next */
        return (
          <StatusChip
            status={'warning'}
            label={translate(get(messages, 'statusCreated'), props)}
          />
        );
      case REQUESTS_STATUSES.VALIDATED:
        /* istanbul ignore next */
        return (
          <StatusChip
            status={'success'}
            label={translate(get(messages, 'statusValidated'), props)}
          />
        );
      case REQUESTS_STATUSES.REJECTED:
        /* istanbul ignore next */
        return (
          <StatusChip
            status={'error'}
            label={translate(get(messages, 'statusRejected'), props)}
          />
        );
    }
  };

  if (isLoading) {
    /* istanbul ignore next */
    return <CircularProgress color="inherit" size={20} />;
  }

  return (
    <>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <Box width="100%">
          <Grid container justify="space-between">
            <Grid item xs={12} sm={3}>
              <Grid item>
                <StyledCaption>
                  {translate(get(messages, 'requestDate'), props)}
                </StyledCaption>
              </Grid>
              <Grid item>
                <Box marginBottom={0}>
                  <StyledShortBody>{request.DateCreation}</StyledShortBody>
                </Box>
              </Grid>
            </Grid>
            <Grid item xs={6} sm>
              <Grid container direction="column">
                <Grid item>
                  <StyledCaption>
                    {translate(get(messages, 'category'), props)}
                  </StyledCaption>
                </Grid>
                <Grid item>
                  <ShortBody>{request.IdCategorie}</ShortBody>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={6} sm>
              <Grid container direction="column">
                <Grid item>
                  <StyledCaption>
                    {translate(get(messages, 'genre'), props)}
                  </StyledCaption>
                </Grid>
                <Grid item>
                  <ShortBody>
                    {request.Genre === 'F'
                      ? translate(get(messages, 'female'), props)
                      : translate(get(messages, 'male'), props)}
                  </ShortBody>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={6} sm>
              <Grid container direction="column">
                <Grid item>
                  <StyledCaption>
                    {translate(get(messages, 'status'), props)}
                  </StyledCaption>
                </Grid>
                <Grid item>
                  <Box marginBottom={0}>{renderDetails(request)}</Box>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={6} sm>
              <Grid container direction="column">
                <Grid item>
                  <StyledCaption>
                    {translate(get(messages, 'folderReference'), props)}
                  </StyledCaption>
                </Grid>
                <Grid item>
                  <ShortBody>{request.Reference}</ShortBody>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </AccordionSummary>
      {expanded && index === expanded && (
        <StyledAccordionDetails>
          <Box width={BOX_SIZE_BY_REQUEST_STATUS[request.Statut] ?? '100%'}>
            <Grid container justify="space-between">
              <Grid item xs={6} sm>
                <Grid container direction="column">
                  <Grid item>
                    <StyledCaption>
                      {translate(get(messages, 'personneCharge'), props)}
                    </StyledCaption>
                  </Grid>
                  <Grid item>
                    <ShortBody>{request.NomPrenomPAC}</ShortBody>
                  </Grid>
                </Grid>
              </Grid>
              {request.MotifRejet && (
                <Grid item xs={6} sm>
                  <Grid container direction="column">
                    <Grid item>
                      <StyledCaption>
                        {translate(get(messages, 'motif'), props)}
                      </StyledCaption>
                    </Grid>
                    <Grid item>
                      <Grid item>
                        <ShortBody>{request.MotifRejet}</ShortBody>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              )}
              {request.ReferenceDemande && (
                <Grid item xs={6} sm>
                  <Grid container direction="column">
                    <Grid item>
                      <StyledCaption>
                        {translate(get(messages, 'numDeclaration'), props)}
                      </StyledCaption>
                    </Grid>
                    <Grid item>
                      <Grid item>
                        <ShortBody>{request.ReferenceDemande}</ShortBody>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              )}
              <Grid item xs={6} sm>
                <Grid container direction="column">
                  <Grid item>
                    <StyledCaption>
                      {translate(get(messages, 'bday'), props)}
                    </StyledCaption>
                  </Grid>
                  <Grid item>
                    <Grid item>
                      <ShortBody>{request.DateNaissanceFormatted}</ShortBody>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={6} sm></Grid>
            </Grid>
          </Box>
        </StyledAccordionDetails>
      )}
    </>
  );
};

export default injectIntl(RequestDetails);
